// AXIS ZERO - Obstacles Module
// VISUAL: Dark interior with neon outline, minimal style
// Handles obstacle spawning, movement, collision detection

import { CONFIG } from './Config';

let obstacleIdCounter = 0;

export class Obstacle {
  constructor(x, width, height, isTwist = false, telegraphDelay = 0, variant = null) {
    this.id = `obs_${obstacleIdCounter++}_${Date.now()}`;
    this.x = x;
    this.width = width;
    this.height = height;
    this.isTwist = isTwist;
    this.telegraphDelay = telegraphDelay;
    this.isActive = !isTwist;
    this.activationTimer = 0;
    this.blinkPhase = 0;
    this.passed = false;
    this.nearMissRegistered = false;
    
    // Near miss glow pulse
    this.nearMissGlow = 0;
    
    // Variant for slight size variation (visual only)
    this.variant = variant || CONFIG.OBSTACLE_VARIANTS[Math.floor(Math.random() * CONFIG.OBSTACLE_VARIANTS.length)];
  }

  update(deltaTime, speed, timeScale = 1) {
    const dt = deltaTime * timeScale;
    
    // Move obstacle (DO NOT CHANGE)
    this.x -= speed * dt;
    
    // Handle twist obstacle activation (DO NOT CHANGE)
    if (this.isTwist && !this.isActive) {
      this.activationTimer += dt;
      this.blinkPhase += dt * 15;
      
      if (this.activationTimer >= this.telegraphDelay) {
        this.isActive = true;
      }
    }
    
    // Decay near-miss glow
    if (this.nearMissGlow > 0) {
      this.nearMissGlow = Math.max(0, this.nearMissGlow - dt * 5);
    }
  }

  triggerNearMissGlow() {
    this.nearMissGlow = 1;
  }

  draw(ctx, groundY, worldColors = null) {
    const colors = worldColors || CONFIG.COLORS;
    const obstacleY = groundY - this.height;
    const cornerRadius = 3;

    ctx.save();

    // Twist obstacle blinking
    if (this.isTwist && !this.isActive) {
      const alpha = Math.sin(this.blinkPhase) * 0.4 + 0.5;
      ctx.globalAlpha = alpha * 0.6;
    }

    const glowIntensity = 12 + this.nearMissGlow * 18;

    // Solid obstacle: FILL ONLY (no outline, no glow) - most stable
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;

    ctx.fillStyle = colors.OBSTACLE_STROKE;
    ctx.lineWidth = 0;

    this.drawRoundedRect(ctx, this.x, obstacleY, this.width, this.height, cornerRadius);
    ctx.fill();

    ctx.restore();
  }

  drawRoundedRect(ctx, x, y, width, height, radius) {
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
  }

  getBounds() {
    // DO NOT CHANGE hitbox
    return {
      x: this.x,
      y: 0,
      width: this.width,
      height: this.height,
    };
  }

  isOffScreen() {
    return this.x + this.width < 0;
  }
}

export class ObstacleManager {
  constructor() {
    this.obstacles = [];
    this.nextSpawnX = 600;
    this.obstacleIndex = 0;
  }

  reset() {
    this.obstacles = [];
    this.nextSpawnX = 600;
    this.obstacleIndex = 0;
  }

  spawnObstacle(canvasWidth, pattern, speed, gameTime = 0) {
    // DO NOT CHANGE spawn logic
    const { OBSTACLE_MIN_WIDTH, OBSTACLE_MAX_WIDTH, OBSTACLE_MIN_HEIGHT, OBSTACLE_MAX_HEIGHT } = CONFIG;
    
    const width = OBSTACLE_MIN_WIDTH + Math.random() * (OBSTACLE_MAX_WIDTH - OBSTACLE_MIN_WIDTH);
    const heightRange = pattern.heightRange;
    const minH = OBSTACLE_MIN_HEIGHT + (OBSTACLE_MAX_HEIGHT - OBSTACLE_MIN_HEIGHT) * heightRange[0];
    const maxH = OBSTACLE_MIN_HEIGHT + (OBSTACLE_MAX_HEIGHT - OBSTACLE_MIN_HEIGHT) * heightRange[1];
    const height = minH + Math.random() * (maxH - minH);
    
    const isTwist = pattern.hasTelegraph;
    const telegraphDelay = isTwist ? pattern.telegraphDelay : 0;
    
    const variantIndex = this.obstacleIndex % CONFIG.OBSTACLE_VARIANTS.length;
    const variant = CONFIG.OBSTACLE_VARIANTS[variantIndex];
    
    const obstacle = new Obstacle(
      this.nextSpawnX,
      width,
      height,
      isTwist,
      telegraphDelay,
      variant
    );
    
    this.obstacles.push(obstacle);
    this.obstacleIndex++;
    
    // DO NOT CHANGE gap calculation
    let baseGap = CONFIG.OBSTACLE_GAP_MIN + Math.random() * (CONFIG.OBSTACLE_GAP_MAX - CONFIG.OBSTACLE_GAP_MIN);
    
    if (gameTime < CONFIG.EARLY_GAME_DURATION) {
      baseGap *= CONFIG.EARLY_GAME_GAP_MULT;
    }
    
    const gap = Math.max(baseGap * pattern.gapMultiplier, CONFIG.MIN_SAFE_GAP);
    this.nextSpawnX += width + gap;
    
    return obstacle;
  }

  update(deltaTime, speed, canvasWidth, groundY, timeScale = 1) {
    this.obstacles.forEach(obstacle => {
      obstacle.update(deltaTime, speed, timeScale);
    });
    
    this.obstacles = this.obstacles.filter(obs => !obs.isOffScreen());
    this.nextSpawnX -= speed * deltaTime * timeScale;
  }

  draw(ctx, groundY, worldColors = null) {
    this.obstacles.forEach(obstacle => {
      obstacle.draw(ctx, groundY, worldColors);
    });
  }

  checkCollision(playerBounds, groundY) {
    // DO NOT CHANGE collision detection
    for (const obstacle of this.obstacles) {
      if (obstacle.isTwist && !obstacle.isActive) continue;
      
      const obsBounds = obstacle.getBounds();
      const obsY = groundY - obsBounds.height;
      
      if (
        playerBounds.x < obsBounds.x + obsBounds.width &&
        playerBounds.x + playerBounds.width > obsBounds.x &&
        playerBounds.y < obsY + obsBounds.height &&
        playerBounds.y + playerBounds.height > obsY
      ) {
        return { collided: true, obstacle };
      }
    }
    return { collided: false, obstacle: null };
  }

  checkNearMiss(playerBounds, groundY) {
    // Near-miss detection (front OR back edge)
    const threshold = CONFIG.NEAR_MISS_DISTANCE;
    const frontWindowPx = 20;

    for (const obstacle of this.obstacles) {
      if (obstacle.nearMissRegistered) continue;
      if (obstacle.isTwist && !obstacle.isActive) continue;

      const obsBounds = obstacle.getBounds();
      const obsY = groundY - obsBounds.height;

      const playerBottom = playerBounds.y + playerBounds.height;
      const verticalDist = Math.abs(playerBottom - obsY);
      if (verticalDist >= threshold) continue;

      const playerFront = playerBounds.x + playerBounds.width;
      const playerBack = playerBounds.x;

      // FRONT edge: when player front enters a small window at obstacle start
      const isFrontNearMiss = (playerFront > obsBounds.x) && (playerFront < obsBounds.x + frontWindowPx);

      // BACK edge: original logic - when player fully passes obstacle
      const isBackPassed = (playerBack > obsBounds.x + obsBounds.width);

      if (isFrontNearMiss || isBackPassed) {
        obstacle.nearMissRegistered = true;
        obstacle.triggerNearMissGlow();
        if (isBackPassed) obstacle.passed = true;
        return { isNearMiss: true, obstacle, obstacleId: obstacle.id };
      }

      // keep original passed flag behavior
      if (isBackPassed) {
        obstacle.passed = true;
      }
    }

    return { isNearMiss: false, obstacle: null, obstacleId: null };
  }

  shouldSpawnMore(canvasWidth) {
    return this.nextSpawnX < canvasWidth + 200;
  }
  
  // Get the last passed obstacle for near-miss spark position
  getLastPassedObstacle() {
    for (let i = this.obstacles.length - 1; i >= 0; i--) {
      if (this.obstacles[i].passed && this.obstacles[i].nearMissRegistered) {
        return this.obstacles[i];
      }
    }
    return null;
  }
}

export default ObstacleManager;
